import React, { useEffect, useRef, useState } from 'react';
import { Typography, Empty, Spin, Alert, message, Button, Modal, List, Tag, Space, Input, Dropdown, App } from 'antd';
import { ToolOutlined, EyeOutlined, SearchOutlined, PlusOutlined, EditOutlined, MoreOutlined, DeleteOutlined, InboxOutlined } from '@ant-design/icons';
import { io, Socket } from 'socket.io-client';
import { useTeamCollabStore } from '../../stores/teamCollabStore';
import { useAuthStore } from '../../stores/authStore';
import api from '../../services/api';
import { getRecentTasks, createCollabTask, getTaskDetail, CollabTask, TaskProgress, WorkSession, getWorkSessions, createWorkSession, updateWorkSession, deleteWorkSession } from '../../services/teamCollabApi';
import TaskCard from './TaskCard';
import InputBar from './InputBar';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './TeamCollab.css';

const { Title, Text } = Typography;

interface DraftState {
  command: string;
  fileContent?: string;
  fileName?: string;
}


const cleanTaskCommand = (cmd: string = ''): string => {
  const userPart = cmd.match(/## 用户指令:\s*\n([\s\S]*?)$/);
  if (userPart) return userPart[1].trim();
  return cmd.trim();
};

const formatDuration = (ms?: number): string => {
  if (!ms) return '';
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}秒`;
  return `${(ms / 60000).toFixed(1)}分钟`;
};

const buildFullTaskContext = (task: CollabTask): string => {
  const parts: string[] = [];
  parts.push('以下是智能工坊当前登录用户的一次历史任务完整上下文。请注意：这不是新任务本身，而是供你理解上下文、复盘决策、延续处理的历史资料。');
  parts.push('');
  parts.push('## 历史任务基本信息');
  parts.push(`- 任务ID: ${task.id}`);
  parts.push(`- 状态: ${task.status}`);
  parts.push(`- 创建时间: ${task.createdAt ? new Date(task.createdAt).toLocaleString('zh-CN') : '未知'}`);
  if (task.elapsedMs) parts.push(`- 耗时: ${formatDuration(task.elapsedMs)}`);
  if (task.totalTokens) parts.push(`- 消耗: ${(task.totalTokens / 1000).toFixed(1)}K tokens`);
  if (task.resultType) parts.push(`- 输出类型: ${task.resultType}`);
  if (task.attachedFileName) parts.push(`- 引用文件: ${task.attachedFileName}`);
  if (task.resultFileName) parts.push(`- 产物文件: ${task.resultFileName}`);
  parts.push('');
  parts.push('## 任务发起内容');
  parts.push(cleanTaskCommand(task.command || '') || '（无）');
  parts.push('');
  if (task.summary) {
    parts.push('## AI任务摘要');
    parts.push(task.summary);
    parts.push('');
  }
  if (task.result) {
    parts.push('## AI任务处理结果 / 最终回复');
    parts.push(task.result);
    parts.push('');
  }
  if (task.thinkingContent) {
    parts.push('## AI处理过程 / 思考记录');
    parts.push(task.thinkingContent.slice(0, 12000));
    if (task.thinkingContent.length > 12000) parts.push('\n（思考记录过长，已截取前12000字符）');
    parts.push('');
  }
  if (task.fileContent) {
    parts.push('## 当时引用的文件内容摘要');
    parts.push(`文件名: ${task.attachedFileName || '未知'}`);
    parts.push(task.fileContent.slice(0, 8000));
    if (task.fileContent.length > 8000) parts.push('\n（文件内容过长，已截取前8000字符）');
    parts.push('');
  }
  parts.push('## 使用要求');
  parts.push('请基于上面这次历史任务的发起内容、AI处理结果、过程记录和产物信息，理解当时从头到尾发生了什么；如果我接下来要求继续、修正、复用、对比或扩展，请以上述完整历史为上下文，不要只参考任务发起内容。');
  return parts.join(String.fromCharCode(10));
};

/** 辅助：滚动到底部哨兵 */
const scrollToBottom = (ref: React.RefObject<HTMLDivElement | null>, smooth = false) => {
  ref.current?.scrollIntoView({ block: 'end', behavior: smooth ? 'smooth' : 'instant' });
};

const TeamCollabPage: React.FC = () => {
  // antd v5：静态 Modal.confirm 在 <App> + 自定义主题下弹不出来（删除会话没反应的根因），
  // 改用上下文版 modal，正确继承主题并可靠弹出。
  const { modal } = App.useApp();
  const { tasks, loading, setTasks, addTask, updateTask, setLoading, addProgress, mergeFromServer } = useTeamCollabStore();
  const authUser = useAuthStore((s) => s.user);
  const authToken = useAuthStore((s) => s.token);
  const currentUserId = authUser?.id || '';
  const [sessions, setSessions] = useState<WorkSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(localStorage.getItem('workroom_active_session_id'));
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const [llmError, setLlmError] = useState<string | null>(null);
  const [draft, setDraft] = useState<DraftState>({ command: '' });
  const initialLoadDone = useRef(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const skipRef = useRef(0);
  const PAGE_SIZE = 10;
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyTasks, setHistoryTasks] = useState<CollabTask[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [selectedHistoryTask, setSelectedHistoryTask] = useState<CollabTask | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  // 当前会话历史分页+搜索
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historySearch, setHistorySearch] = useState('');
  const [expandedHistoryIds, setExpandedHistoryIds] = useState<Set<string>>(new Set());
  const [expandedSessionIds, setExpandedSessionIds] = useState<Set<string>>(new Set());
  const historySearchTimer = useRef<any>(null);
  const HISTORY_PAGE_SIZE = 10;
  const [taskSearchVisible, setTaskSearchVisible] = useState(false);
  const [taskSearchKeyword, setTaskSearchKeyword] = useState('');
  const [taskSearchResults, setTaskSearchResults] = useState<CollabTask[]>([]);
  const [taskSearchLoading, setTaskSearchLoading] = useState(false);
  const [taskSearchTotal, setTaskSearchTotal] = useState(0);
  const [openedTaskId, setOpenedTaskId] = useState<string | null>(null);
  const taskSearchTimer = useRef<any>(null);

  useEffect(() => { loadSessionsAndTasks(); }, []);

  const loadSessionsAndTasks = async () => {
    setSessionsLoading(true);
    try {
      const list = await getWorkSessions(false);
      setSessions(list);
      const remembered = localStorage.getItem('workroom_active_session_id');
      const validActive = remembered && list.some(s => s.id === remembered);
      const nextSessionId = validActive ? remembered : (list[0]?.id || null);
      setActiveSessionId(nextSessionId);
      if (nextSessionId) localStorage.setItem('workroom_active_session_id', nextSessionId);
      await loadTasks(true, nextSessionId);
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '加载智能工坊会话失败');
      await loadTasks(true, null);
    } finally {
      setSessionsLoading(false);
    }
  };

  const switchSession = async (sessionId: string) => {
    setActiveSessionId(sessionId);
    localStorage.setItem('workroom_active_session_id', sessionId);
    skipRef.current = 0;
    initialLoadDone.current = false;
    await loadTasks(true, sessionId);
  };

  const handleCreateSession = async () => {
    try {
      const tempId = `temp-${Date.now()}`;
      const now = new Date().toISOString();
      const optimistic: WorkSession = { id: tempId, userId: currentUserId, title: '新会话', isArchived: false, createdAt: now, updatedAt: now, taskCount: 0 };
      setSessions(prev => [optimistic, ...prev]);
      setActiveSessionId(tempId);
      localStorage.setItem('workroom_active_session_id', tempId);
      setTasks([]);
      skipRef.current = 0;
      initialLoadDone.current = true;

      const created = await createWorkSession('新会话');
      setSessions(prev => prev.map(item => item.id === tempId ? created : item));
      setActiveSessionId(created.id);
      localStorage.setItem('workroom_active_session_id', created.id);
      message.success('已新建智能工坊会话');
    } catch (e: any) {
      setSessions(prev => prev.filter(item => !item.id.startsWith('temp-')));
      message.error(e.response?.data?.message || e.message || '新建会话失败');
    }
  };

  const handleRenameSession = async (session: WorkSession) => {
    const title = window.prompt('请输入会话名称', session.title || '新会话');
    if (!title || !title.trim() || title.trim() === session.title) return;
    try {
      const updated = await updateWorkSession(session.id, { title: title.trim() });
      setSessions(prev => prev.map(item => item.id === session.id ? { ...item, ...updated } : item));
      message.success('会话已重命名');
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '重命名失败');
    }
  };

  const handleArchiveSession = async (session: WorkSession) => {
    try {
      await updateWorkSession(session.id, { isArchived: true });
      const refreshed = await getWorkSessions(false);
      setSessions(refreshed);
      if (session.id === activeSessionId) {
        const nextSessionId = refreshed.find(item => item.id !== session.id)?.id || refreshed[0]?.id || null;
        setActiveSessionId(nextSessionId);
        if (nextSessionId) localStorage.setItem('workroom_active_session_id', nextSessionId);
        else localStorage.removeItem('workroom_active_session_id');
        setTasks([]);
        await loadTasks(true, nextSessionId);
      }
      message.success('会话已归档');
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '归档失败');
    }
  };

  const handleDeleteSession = async (session: WorkSession) => {
    try {
      await deleteWorkSession(session.id);
      const refreshed = await getWorkSessions(false);
      setSessions(refreshed);
      if (session.id === activeSessionId) {
        const nextSessionId = refreshed[0]?.id || null;
        setActiveSessionId(nextSessionId);
        if (nextSessionId) localStorage.setItem('workroom_active_session_id', nextSessionId);
        else localStorage.removeItem('workroom_active_session_id');
        setTasks([]);
        await loadTasks(true, nextSessionId);
      }
      message.success('会话已删除');
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '删除失败');
    }
  };

  useEffect(() => {
    if (!authToken) return;
    const socket = io('/ws', { auth: { token: authToken } });
    socketRef.current = socket;

    socket.on('collab:task_started', (data: any) => {
      updateTask(data.taskId, { status: 'running' });
      addProgress(data.taskId, { taskId: data.taskId, step: 'start', detail: '任务开始执行', timestamp: Date.now() });
    });
    socket.on('collab:task_completed', (data: any) => {
      updateTask(data.taskId, {
        status: 'completed', summary: data.summary, resultType: data.resultType,
        resultFileId: data.resultFileId, resultFileName: data.resultFileName,
        totalTokens: data.totalTokens, elapsedMs: data.elapsedMs,
        result: data.result || data.summary,
        thinkingContent: data.thinkingContent || undefined,
      } as any);
      addProgress(data.taskId, { taskId: data.taskId, step: 'done',
        detail: `执行完成 (${data.totalTokens > 0 ? (data.totalTokens / 1000).toFixed(1) + 'K tokens, ' : ''}耗时${(data.elapsedMs / 1000).toFixed(1)}s)`,
        timestamp: Date.now() });
      setTimeout(() => scrollToBottom(bottomRef, true), 100);
    });
    socket.on('collab:task_failed', (data: any) => {
      updateTask(data.taskId, { status: 'failed', result: `❌ 执行失败: ${data.error}` });
      addProgress(data.taskId, { taskId: data.taskId, step: 'error', detail: `执行失败: ${data.error}`, timestamp: Date.now() });
      if (data.error?.includes('未配置LLM') || data.error?.includes('未配置')) setLlmError(data.error);
      setTimeout(() => scrollToBottom(bottomRef, true), 100);
    });
    socket.on('collab:task_progress', (data: TaskProgress) => {
      addProgress(data.taskId, data);
    });

    // 实时接收AI思考过程（节流推送，直接更新thinkingContent）
    socket.on('collab:task_thinking', (data: any) => {
      updateTask(data.taskId, { thinkingContent: data.content } as any);
    });

    // 附件上传/OCR 进度：讯飞OCR识别中的实时状态，用可更新的 loading 提示展示
    socket.on('collab:upload_progress', (data: { fileName: string; stage: string; message: string }) => {
      const key = `upload-${data.fileName}`;
      if (data.stage === 'done') {
        message.success({ key, content: `${data.fileName}：${data.message}`, duration: 2 });
      } else if (data.stage === 'error') {
        message.error({ key, content: `${data.fileName}：${data.message}`, duration: 3 });
      } else {
        message.loading({ key, content: `${data.fileName}：${data.message}`, duration: 0 });
      }
    });

    return () => { socket.disconnect(); };
  }, [authToken, addProgress, updateTask]);

  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const hasActiveRef = useRef(false);

  // 轮询：纯 ref 驱动，不依赖 tasks 引用变化，避免每�� WebSocket 事件都销毁重建定时器导致页面卡死
  useEffect(() => {
    const startPolling = () => {
      if (pollingTimerRef.current) return;
      pollingTimerRef.current = setInterval(async () => {
        try {
          const data = await getRecentTasks(0, PAGE_SIZE, undefined, activeSessionId);
          mergeFromServer(data);
          // 刷新活跃状态
          const { tasks: currentTasks } = useTeamCollabStore.getState();
          hasActiveRef.current = currentTasks.some(t => t.status === 'pending' || t.status === 'running');
          if (!hasActiveRef.current && pollingTimerRef.current) {
            clearInterval(pollingTimerRef.current);
            pollingTimerRef.current = null;
          }
        } catch (_) {}
      }, 3000);
    };

    // 当 store 变化时检查是否需要启停
    const unsub = useTeamCollabStore.subscribe((state) => {
      const hasActive = state.tasks.some(t => t.status === 'pending' || t.status === 'running');
      const wasActive = hasActiveRef.current;
      hasActiveRef.current = hasActive;
      if (hasActive && !wasActive) {
        startPolling();
      }
      if (!hasActive && wasActive && pollingTimerRef.current) {
        clearInterval(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    });

    // 初始检查
    const hasActive = tasks.some(t => t.status === 'pending' || t.status === 'running');
    hasActiveRef.current = hasActive;
    if (hasActive) startPolling();

    return () => {
      unsub();
      if (pollingTimerRef.current) { clearInterval(pollingTimerRef.current); pollingTimerRef.current = null; }
    };
  }, [mergeFromServer, activeSessionId]);

  // 往上翻加载更多（不触发滚动）
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el.scrollTop < 50 && hasMore && !loadingMore && !loading) {
        loadTasks(false);
      }
    };
    el.addEventListener('scroll', onScroll);
    return () => el.removeEventListener('scroll', onScroll);
  }, [hasMore, loadingMore, loading, tasks.length]);

  // 初始加载完成后滚动到底部（最新任务）—— 用 instant 避免 smooth 动画被内容高度变化干扰
  useEffect(() => {
    if (!loading && tasks.length > 0 && !initialLoadDone.current) {
      initialLoadDone.current = true;
      // 使用 requestAnimationFrame 确保 DOM 已提交，再用 instant 瞬间到位
      requestAnimationFrame(() => {
        scrollToBottom(bottomRef, false);
        // 二次保障：500ms 后再滚一次，防止 ReactMarkdown 异步渲染导致高度延迟变化
        setTimeout(() => scrollToBottom(bottomRef, false), 500);
      });
    }
  }, [loading, tasks.length]);

  const loadTasks = async (initial: boolean = false, sessionIdOverride?: string | null) => {
    if (initial) setLoading(true); else setLoadingMore(true);
    try {
      const skip = initial ? 0 : skipRef.current;
      const data = await getRecentTasks(skip, PAGE_SIZE, undefined, sessionIdOverride !== undefined ? sessionIdOverride : activeSessionId);
      if (initial) {
        setTasks(data);
        skipRef.current = data.length;
      } else {
        // prepend older tasks
        const existingIds = new Set(tasks.map(t => t.id));
        const newTasks = data.filter(t => !existingIds.has(t.id));
        setTasks([...newTasks, ...tasks]);
        skipRef.current += data.length;
      }
      setHasMore(data.length >= PAGE_SIZE);
    } catch (e) { console.error(e); }
    finally { if (initial) setLoading(false); else setLoadingMore(false); }
  };

  const handleSubmit = async (command: string, resultType: string, kbDepth: number, fileContent?: string, fileName?: string, taskType?: string, useKb?: boolean, isPrivate?: boolean, enableThinking?: boolean, modelId?: string) => {
    setLlmError(null);
    const finalCommand = command;
    try {
      const task = await createCollabTask(finalCommand, resultType, kbDepth, fileContent, fileName, taskType, useKb, isPrivate, enableThinking, modelId, activeSessionId);
      addTask(task);
      if (task.sessionId && task.sessionId !== activeSessionId) {
        setActiveSessionId(task.sessionId);
        localStorage.setItem('workroom_active_session_id', task.sessionId);
      }
      try {
        const refreshed = await getWorkSessions(false);
        setSessions(refreshed);
      } catch {}
      setDraft({ command: '' });
      setTimeout(() => scrollToBottom(bottomRef, true), 100);
    } catch (e: any) {
      const errMsg = e.response?.data?.message || e.message || '创建任务失败';
      if (errMsg.includes('未配置') || errMsg.includes('LLM')) setLlmError(errMsg);
      else message.error(errMsg);
    }
  };

  // "我来试试" 回调：将任务内容填入输入框
  const handleTryIt = async (task: CollabTask) => {
    let cmd = task.command;
    let fileContent: string | undefined = undefined;
    let fileName: string | undefined = task.attachedFileName || undefined;

    // 如果有附件，从后端获取文件内容（后端会自动找同名任务恢复）
    if (task.attachedFileName) {
      try {
        const fileRes = await api.get(`/workroom/tasks/${task.id}/file-content`);
        fileContent = fileRes.data?.fileContent || undefined;
        if (fileContent) message.success(`📄 文件已解析就绪: ${task.attachedFileName} (${fileContent.length.toLocaleString()} 字符)`);
      } catch {}
    }

    // 如果API没返回fileContent，尝试从command字段解析（兼容旧数据）
    if (!fileContent) {
      const fileHeaderIdx = cmd.indexOf('以下是你需要分析的文件内容');
      if (fileHeaderIdx > -1) {
        const codeBlockStart = cmd.indexOf('\n```\n', fileHeaderIdx);
        if (codeBlockStart > -1) {
          const contentStart = codeBlockStart + 5;
          const codeBlockEnd = cmd.indexOf('\n```\n', contentStart);
          if (codeBlockEnd > -1) {
            fileContent = cmd.slice(contentStart, codeBlockEnd).trim();
          } else {
            const altEnd = cmd.lastIndexOf('\n```');
            if (altEnd > contentStart) {
              fileContent = cmd.slice(contentStart, altEnd).trim();
            }
          }
        }

        const userPart = cmd.indexOf('## 用户指令:', fileHeaderIdx);
        if (userPart > -1) {
          cmd = cmd.slice(userPart + '## 用户指令:'.length).trim();
        }
      }
    }

    setDraft({
      command: cmd,
      fileContent: fileContent || undefined,
      fileName: task.attachedFileName || undefined,
    });
    message.success(task.attachedFileName
      ? `已填入指令和文件引用: ${task.attachedFileName}${fileContent ? ` (${fileContent.length.toLocaleString()} 字符)` : ''}`
      : '已填入指令，可修改后发送');
    setTimeout(() => scrollToBottom(bottomRef, true), 100);
  };

  const openHistoryModal = async () => {
    setHistoryVisible(true);
    setHistoryPage(0);
    setHistorySearch('');
    setHistoryTasks([]);
    loadHistoryTasks(0, '');
  };

  const openTaskSearchModal = async () => {
    setTaskSearchVisible(true);
    setTaskSearchKeyword('');
    await loadTaskSearchResults('');
  };

  useEffect(() => {
    if (taskSearchTimer.current) clearTimeout(taskSearchTimer.current);
    if (!taskSearchVisible) return;
    taskSearchTimer.current = setTimeout(() => {
      loadTaskSearchResults(taskSearchKeyword);
    }, 400);
  }, [taskSearchKeyword, taskSearchVisible]);

  const loadTaskSearchResults = async (keyword: string) => {
    setTaskSearchLoading(true);
    try {
      const data = await getRecentTasks(0, 50, keyword.trim() || undefined, null);
      const filtered = data.filter(t => t.status === 'completed' || t.status === 'failed' || t.status === 'running');
      setTaskSearchResults(filtered);
      setTaskSearchTotal(filtered.length);
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '搜索任务失败');
      setTaskSearchResults([]);
      setTaskSearchTotal(0);
    } finally {
      setTaskSearchLoading(false);
    }
  };

  const jumpToTaskSession = async (task: CollabTask) => {
    if (!task.sessionId) {
      message.warning('该任务没有关联会话，无法跳转到会话记录');
      return;
    }
    setTaskSearchVisible(false);
    setOpenedTaskId(task.id);
    await switchSession(task.sessionId);
    requestAnimationFrame(() => {
      setTimeout(() => {
        const el = document.querySelector(`[data-task-id="${task.id}"]`);
        if (el) el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        else scrollToBottom(bottomRef, false);
      }, 150);
    });
    message.success('已跳转到当时的会话记录');
  };

  // 搜索框值变化时防抖走搜索
  useEffect(() => {
    if (historySearchTimer.current) clearTimeout(historySearchTimer.current);
    if (!historyVisible) return;
    historySearchTimer.current = setTimeout(() => {
      setHistoryPage(0);
      loadHistoryTasks(0, historySearch);
    }, 400);
  }, [historySearch]);

  const loadHistoryTasks = async (page: number, search: string) => {
    setHistoryLoading(true);
    try {
      const skip = search ? 0 : page * HISTORY_PAGE_SIZE;
      const take = search ? 100 : HISTORY_PAGE_SIZE;
      const data = await getRecentTasks(skip, take, search || undefined, activeSessionId);
      const filtered = data.filter(t => t.status === 'completed' || t.status === 'failed');
      setHistoryTasks(filtered);
      if (search) {
        setHistoryTotal(filtered.length);
      } else {
        setHistoryTotal(page * HISTORY_PAGE_SIZE + filtered.length + (filtered.length === HISTORY_PAGE_SIZE ? HISTORY_PAGE_SIZE : 0));
      }
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '加载历史失败');
    } finally {
      setHistoryLoading(false);
    }
  };

  const viewHistoryTask = async (task: CollabTask) => {
    setHistoryDetailLoading(true);
    try {
      const detail = await getTaskDetail(task.id);
      setSelectedHistoryTask(detail);
    } catch (e: any) {
      message.error(e.response?.data?.message || e.message || '加载任务详情失败');
    } finally {
      setHistoryDetailLoading(false);
    }
  };

  const selectedContext = selectedHistoryTask ? buildFullTaskContext(selectedHistoryTask) : '';
  const toggleHistoryExpanded = (id: string) => {
    setExpandedHistoryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSessionExpanded = (id: string) => {
    setExpandedSessionIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const sortedTasks = [...tasks].sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

  const activeSession = sessions.find(item => item.id === activeSessionId) || null;
  return (
    <div className="tc-page tc-workroom-page">
      <div className="tc-header">
        <ToolOutlined className="tc-header-icon" />
        <Title level={5} className="tc-header-title">智能工坊</Title>
        <Text className="tc-header-subtitle">快速生成文档、表格、报价等工作文件</Text>
        <div style={{ flex: 1 }} />
        <Button className="tc-task-search-entry" icon={<SearchOutlined />} onClick={openTaskSearchModal}>
          搜索任务
        </Button>
      </div>
      {llmError && <Alert message={llmError} type="warning" showIcon closable onClose={() => setLlmError(null)} style={{ margin: '8px 16px 0', borderRadius: 8, flexShrink: 0 }} />}
      <div className="tc-workroom-shell">
        <aside className="tc-workroom-side">
          <div className="tc-session-panel">
            <div className="tc-session-panel-head">
              <div>
                <div className="tc-session-panel-title">会话空间</div>
                <div className="tc-session-panel-subtitle">{activeSession?.title || '选择或新建会话'}</div>
              </div>
              <Button size="small" type="primary" icon={<PlusOutlined />} onClick={handleCreateSession}>新建</Button>
            </div>
            <Spin spinning={sessionsLoading}>
              <div className="tc-session-list">
                {sessions.length === 0 ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无会话" /> : sessions.map(session => {
                  const lastCommand = cleanTaskCommand(session.lastCommand || '');
                  const expanded = expandedSessionIds.has(session.id);
                  return (
                  <div key={session.id} className={`tc-session-item ${session.id === activeSessionId ? 'is-active' : ''}`} onClick={() => switchSession(session.id)}>
                    <div className="tc-session-title-row">
                      <span className="tc-session-title">{session.title || '新会话'}</span>
                      <Dropdown
                        trigger={['click']}
                        menu={{
                          items: [
                            { key: 'rename', icon: <EditOutlined />, label: '重命名' },
                            { key: 'archive', icon: <InboxOutlined />, label: '归档' },
                            { key: 'delete', icon: <DeleteOutlined />, label: '删除', danger: true },
                          ],
                          onClick: ({ key, domEvent }) => {
                            domEvent.stopPropagation();
                            if (key === 'rename') handleRenameSession(session);
                            if (key === 'archive') handleArchiveSession(session);
                            if (key === 'delete') {
                              modal.confirm({
                                className: 'df-readable-modal',
                                title: '删除会话？',
                                content: '删除后会话入口会移除，历史任务将保留为未归属会话。',
                                okText: '删除',
                                okButtonProps: { danger: true },
                                cancelText: '取消',
                                onOk: () => handleDeleteSession(session),
                              });
                            }
                          },
                        }}
                      >
                        <Button
                          size="small"
                          type="text"
                          icon={<MoreOutlined />}
                          aria-label="会话更多操作"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </Dropdown>
                    </div>
                    <div className="tc-session-meta">{session.taskCount || 0} 个任务 · {session.updatedAt ? new Date(session.updatedAt).toLocaleString('zh-CN') : ''}</div>
                    {lastCommand && (
                      <>
                        <div className={`tc-session-last ${expanded ? 'is-expanded' : ''}`}>{lastCommand}</div>
                        {lastCommand.length > 56 && (
                          <Button
                            size="small"
                            type="link"
                            className="tc-session-detail-toggle"
                            onClick={(e) => { e.stopPropagation(); toggleSessionExpanded(session.id); }}
                          >
                            {expanded ? '收起具体信息' : '展开查看具体信息'}
                          </Button>
                        )}
                      </>
                    )}
                  </div>
                );})}
              </div>
            </Spin>
          </div>
        </aside>
        <section className="tc-workroom-main">
          <div ref={listRef} className="tc-task-list">
        {loading && tasks.length === 0 ? <div className="tc-loading"><Spin /></div>
        : tasks.length === 0 ? <div className="tc-workroom-empty"><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="当前会话暂无任务" /><div className="tc-workroom-empty-tip">在右侧输入任务，或上传 Word / PDF / Excel / 图片，让 AI 在当前会话中持续处理。</div></div>
        : (
          <>
            {sortedTasks.map((task) => (
              <div key={task.id} data-task-id={task.id} className={openedTaskId === task.id ? 'tc-opened-task-anchor is-highlighted' : 'tc-opened-task-anchor'}>
                <TaskCard task={task} currentUserId={currentUserId} onTryIt={handleTryIt} />
              </div>
            ))}
            {/* 底部哨兵：scrollIntoView 定位目标 */}
            <div ref={bottomRef} style={{ height: 1 }} />
          </>
        )}
          </div>
        </section>
        <aside className="tc-workroom-command">
          <InputBar
            onSubmit={handleSubmit}
            loading={tasks.some(t => t.status === 'running')}
            draftCommand={draft.command}
            draftFileName={draft.fileName}
            draftFileContent={draft.fileContent}
            onDraftConsumed={() => setDraft({ command: '' })}
            onOpenHistory={openHistoryModal}
          />
        </aside>
      </div>
      <Modal
        title="搜索任务"
        open={taskSearchVisible}
        onCancel={() => {
          setTaskSearchVisible(false);
          setTaskSearchKeyword('');
          setTaskSearchResults([]);
        }}
        footer={<Button onClick={() => setTaskSearchVisible(false)}>关闭</Button>}
        width="86vw"
        style={{ top: 18, maxWidth: 1180 }}
        className="df-history-modal tc-history-modal tc-task-search-modal"
        rootClassName="df-history-modal-root tc-task-search-modal-root"
      >
        <Input.Search
          placeholder="输入关键词，搜索所有智能工坊会话任务"
          allowClear
          autoFocus
          value={taskSearchKeyword}
          onChange={(e) => setTaskSearchKeyword(e.target.value)}
          onSearch={(val) => {
            setTaskSearchKeyword(val);
            loadTaskSearchResults(val);
          }}
          enterButton="搜索"
          prefix={<SearchOutlined />}
          style={{ marginBottom: 16 }}
        />
        <Spin spinning={taskSearchLoading}>
          {taskSearchResults.length === 0 ? (
            <Empty description={taskSearchKeyword.trim() ? '没有搜索到相关任务' : '输入关键词搜索，或查看最近任务'} />
          ) : (
            <List
              className="tc-task-search-list"
              dataSource={taskSearchResults}
              pagination={taskSearchTotal > 10 ? { pageSize: 10, size: 'small', showTotal: total => `共 ${total} 条` } : false}
              renderItem={(task) => {
                const commandText = cleanTaskCommand(task.command || '') || '无标题任务';
                return (
                  <List.Item
                    actions={[
                      <Button key="open" size="small" type="primary" icon={<EyeOutlined />} onClick={() => jumpToTaskSession(task)}>
                        打开会话
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      title={(
                        <Space size={6} wrap>
                          <Tag color={task.status === 'completed' ? 'green' : task.status === 'running' ? 'processing' : 'red'}>
                            {task.status === 'completed' ? '已完成' : task.status === 'running' ? '执行中' : '失败'}
                          </Tag>
                          <Tag>{task.resultType || 'text'}</Tag>
                          {task.sessionId && <Text type="secondary" style={{ fontSize: 12 }}>会话 {task.sessionId.slice(0, 8)}</Text>}
                          <Text type="secondary" style={{ fontSize: 12 }}>{new Date(task.createdAt).toLocaleString('zh-CN')}</Text>
                        </Space>
                      )}
                      description={(
                        <div>
                          <div className="tc-task-search-command">{commandText}</div>
                          {(task.summary || task.result) && (
                            <div className="tc-task-search-summary">{task.summary || String(task.result || '').slice(0, 180)}</div>
                          )}
                        </div>
                      )}
                    />
                  </List.Item>
                );
              }}
            />
          )}
        </Spin>
      </Modal>
      <Modal
        title="当前会话历史"
        open={historyVisible}
        onCancel={() => { setHistoryVisible(false); setSelectedHistoryTask(null); setHistorySearch(''); }}
        footer={selectedHistoryTask ? [
          <Button key="back" onClick={() => setSelectedHistoryTask(null)}>返回列表</Button>,
          <Button key="close" type="primary" onClick={() => { setHistoryVisible(false); setSelectedHistoryTask(null); }}>关闭</Button>,
        ] : [<Button key="close" onClick={() => { setHistoryVisible(false); setHistorySearch(''); }}>关闭</Button>]}
        width={selectedHistoryTask ? '92vw' : '86vw'}
        style={{ top: 18, maxWidth: selectedHistoryTask ? 1320 : 1180 }}
        className="df-history-modal tc-history-modal"
        rootClassName="df-history-modal-root"
      >
        {!selectedHistoryTask ? (
          <>
            <Input.Search
              placeholder="搜索历史任务内容..."
              allowClear
              value={historySearch}
              onChange={(e) => setHistorySearch(e.target.value)}
              onSearch={(val) => { setHistoryPage(0); loadHistoryTasks(0, val); }}
              style={{ marginBottom: 16 }}
              prefix={<SearchOutlined />}
            />
            <Spin spinning={historyLoading}>
              {historyTasks.length === 0 ? <Empty description="当前会话暂无历史任务" /> : (
                <List
                  dataSource={historyTasks}
                  pagination={historySearch ? false : {
                    current: historyPage + 1,
                    pageSize: HISTORY_PAGE_SIZE,
                    total: historyTotal,
                    onChange: (p) => { const page = p - 1; setHistoryPage(page); loadHistoryTasks(page, historySearch); },
                    showTotal: (t) => `共 ${t} 条`,
                    size: 'small',
                  }}
                  className="tc-history-list"
                  renderItem={(task) => {
                    const commandText = cleanTaskCommand(task.command || '') || '无标题任务';
                    const expanded = expandedHistoryIds.has(task.id);
                    return (
                    <List.Item
                      actions={[
                        <Button size="small" type="link" onClick={() => toggleHistoryExpanded(task.id)}>{expanded ? '收起' : '展开'}</Button>,
                        <Button size="small" icon={<EyeOutlined />} onClick={() => viewHistoryTask(task)} loading={historyDetailLoading}>查看</Button>,
                        
                      ]}
                    >
                      <List.Item.Meta
                        title={<Space size={6}><Tag color={task.status === 'completed' ? 'green' : 'red'}>{task.status === 'completed' ? '已完成' : '失败'}</Tag><Text type="secondary" style={{fontSize:12}}>任务要求</Text></Space>}
                        description={<div>
                          <div className={`tc-history-command ${expanded ? 'is-expanded' : ''}`}>{commandText}</div>
                          <Text type="secondary">{new Date(task.createdAt).toLocaleString('zh-CN')}</Text>
                        </div>}
                      />
                    </List.Item>
                  );}}

                />
              )}
            </Spin>
          </>
        ) : (
          <div>
            <Alert
              type="info"
              showIcon
              message="这里展示的是当前会话内的历史任务详情"
              description="同一会话会自动带入最近历史上下文，无需手动引用。这里仅用于查看任务发起内容、AI摘要、最终结果和思考过程。"
              style={{ marginBottom: 12 }}
            />
            <div className="tc-preview-context md-content df-rich-history-content">
              {historyDetailLoading ? <Spin /> : <ReactMarkdown remarkPlugins={[remarkGfm]}>{selectedContext || ''}</ReactMarkdown>}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TeamCollabPage;
